import React, { createContext, useContext, useMemo } from 'react';

/**
 * TableContext - Provides shared state and actions to all table components
 * Eliminates prop drilling through TaskCategoryGroup → TaskRow → Cells
 */
const TableContext = createContext(null);

/**
 * Hook to access table context
 * Throws if used outside of TableProvider
 */
export const useTableContext = () => {
  const context = useContext(TableContext);
  if (!context) {
    throw new Error('useTableContext must be used within a TableProvider');
  }
  return context;
};

/**
 * TableProvider - Wraps the table and provides shared state/actions
 */
export const TableProvider = ({
  children,
  // Data
  campaignId,
  allTasks,
  allCategories,
  allTags,
  statuses,
  nestedSprints,
  allSprints = [],
  lockStates,
  // Shared column sizing (synced across all tables)
  columnSizing,
  onColumnSizingChange,
  // Column visibility
  columnVisibility,
  // Actions - Task operations
  onStatusChange,
  onPriorityChange,
  onDifficultyChange,
  onDependencyChange,
  onConditionsChange,
  onDeleteTask,
  onDuplicateTask,
  onAddTask,
  onAddDecision,
  onAddWait,
  onRenameTask,
  onCategoryChange,
  onRenameCategory,
  onDeleteCategory,
  // Actions - Tags (multi-select)
  onAddTag,
  onRemoveTag,
  onSetPrimaryTag,
  onRenameTag,
  onDeleteTag,
  // Actions - Create nested campaign under a mission (flow: Create Campaign Inside Mission)
  onAddSubtask,
  // Actions - Navigation/UI
  onFocusTask,
  onOpenDetails,
  onOpenAttachments,
  onOpenNestedSprint,
  onRemoveNestedSprint,
  // Actions - Icon
  onIconChange,
  // Actions - Archive
  onArchiveTask,
  onUnarchiveTask,
  // Actions - Decision nodes
  onDecisionAnswerChange,
  onDecisionOptionsChange,
  // Actions - Status management
  onAddStatus,
  onDeleteStatus,
  onRenameStatus,
  onSetStatusAutoSync,
  // Actions - Selection
  onSelectionChange,
  // Actions - Delete confirmation
  onOpenDeleteConfirmation,
  // View navigation
  currentView,
  customViews,
  activeCustomViewId,
  // Custom fields
  customFields,
  onCustomFieldValueChange,
  // Deliverables warning modal
  onSubmitDeliverable,
  onNavigateToParentMission,
}) => {
  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    // Data
    campaignId,
    allTasks,
    allCategories,
    allTags,
    statuses,
    nestedSprints,
    allSprints,
    lockStates,
    // Shared column sizing
    columnSizing,
    onColumnSizingChange,
    // Column visibility
    columnVisibility,
    // Actions - Task operations
    onStatusChange,
    onPriorityChange,
    onDifficultyChange,
    onDependencyChange,
    onConditionsChange,
    onDeleteTask,
    onDuplicateTask,
    onAddTask,
    onAddDecision,
    onAddWait,
    onRenameTask,
    onCategoryChange,
    onRenameCategory,
    onDeleteCategory,
    // Actions - Tags (multi-select)
    onAddTag,
    onRemoveTag,
    onSetPrimaryTag,
    onRenameTag,
    onDeleteTag,
    // Actions - Add mission to nested campaign
    onAddSubtask,
    // Actions - Navigation/UI
    onFocusTask,
    onOpenDetails,
    onOpenAttachments,
    onOpenNestedSprint,
    onRemoveNestedSprint,
    // Actions - Icon
    onIconChange,
    // Actions - Archive
    onArchiveTask,
    onUnarchiveTask,
    // Actions - Decision nodes
    onDecisionAnswerChange,
    onDecisionOptionsChange,
    // Actions - Status management
    onAddStatus,
    onDeleteStatus,
    onRenameStatus,
    onSetStatusAutoSync,
    // Actions - Selection
    onSelectionChange,
    // Actions - Delete confirmation
    onOpenDeleteConfirmation,
    // View navigation
    currentView,
    customViews,
    activeCustomViewId,
    // Custom fields
    customFields,
    onCustomFieldValueChange,
    // Deliverables warning modal
    onSubmitDeliverable,
    onNavigateToParentMission,
  }), [
    campaignId,
    allTasks,
    allCategories,
    allTags,
    statuses,
    nestedSprints,
    allSprints,
    lockStates,
    columnSizing,
    onColumnSizingChange,
    columnVisibility,
    onStatusChange,
    onPriorityChange,
    onDifficultyChange,
    onDependencyChange,
    onConditionsChange,
    onDeleteTask,
    onDuplicateTask,
    onAddTask,
    onAddDecision,
    onAddWait,
    onRenameTask,
    onCategoryChange,
    onRenameCategory,
    onDeleteCategory,
    onAddTag,
    onRemoveTag,
    onSetPrimaryTag,
    onRenameTag,
    onDeleteTag,
    onAddSubtask,
    onFocusTask,
    onOpenDetails,
    onOpenAttachments,
    onOpenNestedSprint,
    onRemoveNestedSprint,
    onIconChange,
    onArchiveTask,
    onUnarchiveTask,
    onDecisionAnswerChange,
    onDecisionOptionsChange,
    onAddStatus,
    onDeleteStatus,
    onRenameStatus,
    onSetStatusAutoSync,
    onSelectionChange,
    onOpenDeleteConfirmation,
    currentView,
    customViews,
    activeCustomViewId,
    customFields,
    onCustomFieldValueChange,
    onSubmitDeliverable,
    onNavigateToParentMission,
  ]);

  return (
    <TableContext.Provider value={value}>
      {children}
    </TableContext.Provider>
  );
};

export default TableContext;